import { Injectable, inject } from '@angular/core'
import { SettingsStore } from '@awork/framework/state/settings.store'
import type { IGroupingSorting } from '@awork/framework/state/settings.types'

// Used to save form selection
export interface Selection {
  projectType: string
  workType: string
}

/** Scroll state for one list and its current search/filter/sort context. */
export interface ListScrollPosition {
  context: string
  offset: number
}

@Injectable({
  providedIn: 'root'
})
export class ViewPersistentService {
  private settingsStore = inject(SettingsStore)

  // UI persistent data
  private scrollPositions: { key: string; value: number }[] = []
  private readonly listPositions = new Map<string, ListScrollPosition>()
  private readonly listExpansions = new Map<string, readonly string[]>()
  private activeViews: { key: string; value: string }[] = []
  private doneTaskFilter: { key: string; value: boolean }[] = []

  private searchTerm = ''

  private selection: Selection = {
    projectType: null,
    workType: null
  }

  /**
   * Saves the scroll position for a specific key
   * @param {string} key
   * @param {number} value
   */
  updateScrollPosition(key: string, value: number): void {
    const item = this.scrollPositions.find(x => x.key === key)

    if (item) {
      const index = this.scrollPositions.indexOf(item)

      this.scrollPositions[index] = { key, value }
    } else {
      this.scrollPositions.push({ key, value })
    }
  }

  /**
   * Returns the scroll position for a specific key
   * @param {string} key
   * @returns {number}
   */
  getScrollPosition(key: string): number {
    const item = this.scrollPositions.find(x => x.key === key)

    if (item) {
      return item.value
    } else {
      return 0
    }
  }

  /**
   * Reads session scroll state without applying it to a different list context.
   * @param {string} key Workspace, user, and list key.
   * @returns {ListScrollPosition | undefined} Last recorded list position.
   */
  getListPosition(key: string): ListScrollPosition | undefined {
    return this.listPositions.get(key)
  }

  /**
   * Saves session scroll state when a list scrolls or closes.
   * @param {string} key Workspace, user, and list key.
   * @param {ListScrollPosition} position Current context and offset.
   */
  updateListPosition(key: string, position: ListScrollPosition): void {
    this.listPositions.set(key, position)
  }

  /**
   * Creates a stable list context from explicit filter, sort, and view fields.
   * @param {readonly (string | number | boolean | null | undefined)[]} parts Context fields in display order.
   * @returns {string} Serialized context key.
   */
  createListContext(parts: readonly (string | number | boolean | null | undefined)[]): string {
    return JSON.stringify(parts)
  }

  /**
   * Reads persisted expanded or collapsed entity IDs for one list.
   * @param {string} key List expansion key.
   * @returns {readonly string[]} Saved entity IDs.
   */
  getListExpansion(key: string): readonly string[] {
    return this.listExpansions.get(key) ?? []
  }

  /**
   * Saves expanded or collapsed entity IDs for one list.
   * @param {string} key List expansion key.
   * @param {readonly string[]} ids Entity IDs in the saved state.
   */
  updateListExpansion(key: string, ids: readonly string[]): void {
    this.listExpansions.set(key, [...ids])
  }

  /**
   * Saves the active view for a specific key
   * @param {string} key
   * @param {string} value
   */
  updateActiveView(key: string, value: string): void {
    const item = this.activeViews.find(x => x.key === key)

    if (item) {
      const index = this.activeViews.indexOf(item)

      this.activeViews[index] = { key, value }
    } else {
      this.activeViews.push({ key, value })
    }
  }

  /**
   * Returns the active view for a specific key
   * @param {string} key
   * @returns {string}
   */
  getActiveView(key: string): string {
    const item = this.activeViews.find(x => x.key === key)

    if (item) {
      return item.value
    } else {
      return null
    }
  }

  /**
   * Saves the done tasks filter for a specific key
   * @param {string} key
   * @param {boolean} value
   */
  updateDoneTaskFilter(key: string, value: boolean): void {
    const item = this.doneTaskFilter.find(x => x.key === key)

    if (item) {
      const index = this.doneTaskFilter.indexOf(item)

      this.doneTaskFilter[index] = { key, value }
    } else {
      this.doneTaskFilter.push({ key, value })
    }
  }

  /**
   * Returns the done tasks for a specific key
   * @param {string} key
   * @returns {boolean}
   */
  getDoneTaskFilter(key: string): boolean {
    const item = this.doneTaskFilter.find(x => x.key === key)

    if (item) {
      return item.value
    } else {
      return false
    }
  }

  /**
   * Saves the search term
   * @param {string} term
   */
  saveSearchTerm(term: string): void {
    this.searchTerm = term
  }

  /**
   * Gets the persisted search term
   * @returns {string}
   */
  getSearchTerm(): string {
    return this.searchTerm
  }

  /**
   * Saves the selection
   * @param {keyof Selection} key
   * @param {string} value
   */
  saveSelection(key: keyof Selection, value: string) {
    this.selection[key] = value
  }

  /**
   * Gets the persisted selection
   * @param {keyof Selection} key
   * @returns {string}
   */
  getSelection(key: keyof Selection): string {
    return this.selection[key]
  }

  /**
   * Save Grouping and sorting Options
   * @param {IGroupingSorting} options
   */
  saveGroupingAndSorting(options: IGroupingSorting): void {
    this.settingsStore.updateGroupingAndSortingOptions(options)
  }
}
